import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Home,
  Email,
  Person,
  Badge,
  Lock,
  Phone,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import hfssLogo from '../../assets/hfss_logo.png';

interface RegistrationData {
  email: string;
  name: string;
  nickname: string;
  pin_code: string;
  phone: string;
  terms_accepted: boolean;
  privacy_policy_accepted: boolean;
  shuttle_terms_accepted: boolean;
}

const ShuttleRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<RegistrationData>({
    email: '',
    name: '',
    nickname: '',
    pin_code: '',
    phone: '',
    terms_accepted: false,
    privacy_policy_accepted: false,
    shuttle_terms_accepted: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shuttleTermsOpen, setShuttleTermsOpen] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.terms_accepted || !formData.privacy_policy_accepted || !formData.shuttle_terms_accepted) {
      setError(t('shuttle.registration.mustAcceptAllTerms'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${apiUrl}/public/pilot-register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || t('shuttle.registration.failed'));
      }

      const result = await response.json();
      
      // Save pilot info to localStorage for quick access
      localStorage.setItem('shuttlePilotId', result.id);
      localStorage.setItem('shuttlePilotNickname', result.nickname);
      
      // Redirect to booking page
      navigate('/app/shuttle-booking');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Header */}
      <AppBar position="static" sx={{ background: 'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/')}
            sx={{ mr: 2 }}
          >
            <Home />
          </IconButton>
          <Box
            component="img"
            src={hfssLogo}
            alt="HFSS"
            sx={{ height: 40, mr: 2, cursor: 'pointer' }}
            onClick={() => navigate('/')}
          />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {t('shuttle.registration.title')}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom align="center">
            {t('shuttle.registration.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph align="center">
            {t('shuttle.registration.subtitle')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                required
                type="email"
                label={t('shuttle.registration.email')}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                InputProps={{
                  startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />

              <TextField
                fullWidth
                required
                label={t('shuttle.registration.name')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                InputProps={{
                  startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />

              <TextField
                fullWidth
                required
                label={t('shuttle.registration.nickname')}
                value={formData.nickname}
                onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                helperText={t('shuttle.registration.nicknameHelp')}
                InputProps={{
                  startAdornment: <Badge sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />

              <TextField
                fullWidth
                required
                label={t('shuttle.registration.pin')}
                value={formData.pin_code}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                  setFormData({ ...formData, pin_code: value });
                }}
                helperText={t('shuttle.registration.pinHelp')}
                inputProps={{ maxLength: 4, pattern: '[0-9]*' }}
                InputProps={{
                  startAdornment: <Lock sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />

              <TextField
                fullWidth
                label={t('shuttle.registration.phone')}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                InputProps={{
                  startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />

              {/* Consent Checkboxes Section */}
              <Box sx={{ 
                border: '1px solid', 
                borderColor: 'divider', 
                borderRadius: 1, 
                p: 2, 
                bgcolor: 'grey.50',
                mt: 1
              }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  {t('shuttle.registration.requiredConsents')}
                </Typography>
                
                {/* Terms of Service */}
                <FormControlLabel
                  sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}
                  control={
                    <Checkbox
                      checked={formData.terms_accepted}
                      onChange={(e) => setFormData({ ...formData, terms_accepted: e.target.checked })}
                      color="primary"
                      sx={{ mt: -0.5 }}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t('shuttle.registration.acceptTerms')} {' '}
                      <Link href="https://www.hikeandfly.app/terms" target="_blank" rel="noopener">
                        {t('shuttle.registration.termsLink')}
                      </Link>
                    </Typography>
                  }
                />

                {/* Privacy Policy */}
                <FormControlLabel
                  sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}
                  control={
                    <Checkbox
                      checked={formData.privacy_policy_accepted}
                      onChange={(e) => setFormData({ ...formData, privacy_policy_accepted: e.target.checked })}
                      color="primary"
                      sx={{ mt: -0.5 }}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t('shuttle.registration.acceptPrivacyPolicy')} {' '}
                      <Link href="https://www.hikeandfly.app/privacy" target="_blank" rel="noopener">
                        {t('shuttle.registration.privacyPolicyLink')}
                      </Link>
                    </Typography>
                  }
                />

                {/* Shuttle-specific Terms */}
                <FormControlLabel
                  sx={{ display: 'flex', alignItems: 'flex-start' }}
                  control={
                    <Checkbox
                      checked={formData.shuttle_terms_accepted}
                      onChange={(e) => setFormData({ ...formData, shuttle_terms_accepted: e.target.checked })}
                      color="primary"
                      sx={{ mt: -0.5 }}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t('shuttle.registration.acceptShuttleTerms')} {' '}
                      <Link href="#" onClick={(e) => { e.preventDefault(); setShuttleTermsOpen(true); }}>
                        {t('shuttle.registration.shuttleTermsLink')}
                      </Link>
                    </Typography>
                  }
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/')}
                  disabled={loading}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || !formData.terms_accepted || !formData.privacy_policy_accepted || !formData.shuttle_terms_accepted || formData.pin_code.length !== 4}
                  fullWidth
                >
                  {loading ? <CircularProgress size={24} /> : t('shuttle.registration.submit')}
                </Button>
              </Box>

              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                {t('shuttle.registration.alreadyRegistered')} {' '}
                <Link 
                  component="button"
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/app/shuttle-booking');
                  }}
                >
                  {t('shuttle.registration.bookNow')}
                </Link>
              </Typography>
            </Box>
          </form>
        </Paper>
      </Container>

      {/* Shuttle Terms Dialog */}
      <Dialog
        open={shuttleTermsOpen}
        onClose={() => setShuttleTermsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('shuttle.registration.shuttleTermsTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="h6" gutterBottom>
              {t('shuttle.registration.shuttleTermsHeading')}
            </Typography>
            <Typography paragraph>
              {t('shuttle.registration.shuttleTermsIntro')}
            </Typography>
            
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
              1. {t('shuttle.registration.terms.booking')}
            </Typography>
            <Typography paragraph>
              {t('shuttle.registration.terms.bookingDetails')}
            </Typography>
            
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              2. {t('shuttle.registration.terms.payment')}
            </Typography>
            <Typography paragraph>
              {t('shuttle.registration.terms.paymentDetails')}
            </Typography>
            
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              3. {t('shuttle.registration.terms.cancellation')}
            </Typography>
            <Typography paragraph>
              {t('shuttle.registration.terms.cancellationDetails')}
            </Typography>
            
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              4. {t('shuttle.registration.terms.liability')}
            </Typography>
            <Typography paragraph>
              {t('shuttle.registration.terms.liabilityDetails')}
            </Typography>
            
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              5. {t('shuttle.registration.terms.dataUsage')}
            </Typography>
            <Typography paragraph>
              {t('shuttle.registration.terms.dataUsageDetails')}
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShuttleTermsOpen(false)}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ShuttleRegistrationPage;